import React, { useState } from 'react';
import { toast } from 'sonner';
import { ScrollText, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface TermsLayoutProps {
  roleName: string; 
  onSubmit: () => Promise<void>; 
  isSubmitting: boolean;
  children: React.ReactNode; // 🟢 THIS IS THE KEY FIX: It accepts any document
}

export const ExpertsTermsAndAgreement = ({ roleName, onSubmit, isSubmitting, children }: TermsLayoutProps) => {
  const navigate = useNavigate();
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [acknowledgedDetails, setAcknowledgedDetails] = useState(false);

  const handleInternalSubmit = async () => {
    if (!agreedToTerms || !acknowledgedDetails) {
      toast.error('Please accept the terms and confirm your details to proceed.');
      return;
    }

    try {
      await onSubmit();
      navigate('/expert/application-submitted', { replace: true }); 
    } catch (error) {
      console.error(error);
      toast.error("Submission failed. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-blue-950 px-4 font-sans py-12">
      <div className="max-w-5xl w-full mx-auto animate-in fade-in slide-in-from-bottom-8 duration-700">
        
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-white border-b-4 border-white/50 pb-3 inline-block">
            {roleName} Agreement
          </h1>
          <p className="text-blue-200 text-lg mt-4">
            Please review the trueAdviso Expert Terms & Conditions
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl p-6 md:p-8 shadow-2xl">
          <div className="flex items-center gap-2 mb-4 border-b pb-4 border-gray-100">
            <ScrollText className="w-6 h-6 text-blue-900" />
            <h2 className="text-2xl font-bold text-blue-900">
              Terms & Conditions
            </h2>
          </div>

          {/* SCROLLABLE TERMS BOX */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl h-[500px] overflow-y-auto mb-8 shadow-inner custom-scrollbar relative">
            <div className="p-6 md:p-10">
              
              {/* 🟢 THE SPECIFIC DOCUMENT (Astrology, Doctor, or Lawyer) INJECTS HERE */}
              {children}

            </div>
          </div>

          {/* Checkboxes */}
          <div className="bg-blue-50 rounded-xl p-6 space-y-4">
            <label className="flex items-start gap-4 cursor-pointer group">
              <div className="relative flex items-center">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="peer w-6 h-6 rounded border-2 border-blue-900 text-blue-900 focus:ring-offset-0 focus:ring-2 focus:ring-blue-400 cursor-pointer transition-all"
                />
              </div>
              <div className="flex-1">
                <span className="text-blue-900 font-semibold group-hover:text-blue-700 transition-colors block">
                  I Agree to the Terms & Conditions
                </span>
                <span className="text-sm text-blue-700/80">
                  I have read, understood, and agreed to be bound by the trueAdviso Expert Agreement scrolled above.
                </span>
              </div>
            </label>

            <label className="flex items-start gap-4 cursor-pointer group">
              <div className="relative flex items-center">
                <input
                  type="checkbox"
                  checked={acknowledgedDetails}
                  onChange={(e) => setAcknowledgedDetails(e.target.checked)}
                  className="peer w-6 h-6 rounded border-2 border-blue-900 text-blue-900 focus:ring-offset-0 focus:ring-2 focus:ring-blue-400 cursor-pointer transition-all"
                />
              </div>
              <div className="flex-1">
                <span className="text-blue-900 font-semibold group-hover:text-blue-700 transition-colors block">
                  I Confirm Accuracy of Information
                </span>
                <span className="text-sm text-blue-700/80">
                  I acknowledge that all details provided in my application are accurate and I am 18 years or older.
                </span>
              </div>
            </label>
          </div>

          {/* Submit Button */}
          <div className="mt-8">
            <button
              onClick={handleInternalSubmit}
              disabled={isSubmitting || !agreedToTerms || !acknowledgedDetails}
              className={`w-full py-4 px-8 rounded-xl font-bold text-lg transition-all duration-300 transform 
                ${(agreedToTerms && acknowledgedDetails)
                  ? 'bg-gradient-to-r from-blue-600 to-blue-800 text-white hover:scale-[1.02] shadow-lg'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="animate-spin h-5 w-5" />
                  Processing...
                </span>
              ) : (
                'Accept & Submit Application'
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};